/**
 * Cached WMM magnetic declination for the current fix (spec 14C). Declination
 * varies slowly (a fraction of a degree per year), so recomputing it on every
 * 1 Hz GPS fix would be pure waste — recompute only when the boat has moved
 * more than 50 km since the last evaluation, or the calendar date has
 * changed. Same vanilla-store shape as posStore/gotoMetrics/followMetrics;
 * started once at app boot (main.tsx) so the cache lives for the whole
 * session, not per screen mount.
 */
import { createStore } from 'zustand/vanilla';
import { declinationDeg, haversineMeters, type LatLon } from '@boatnav/core';
import { posStore, type Fix } from './posStore.js';

const RECOMPUTE_DISTANCE_M = 50_000;

interface DeclinationState {
  /** Degrees, positive East; null until the first fix arrives. */
  declDeg: number | null;
}

export const declinationStore = createStore<DeclinationState>(() => ({ declDeg: null }));

let lastFix: LatLon | null = null;
let lastDateKey: string | null = null;
let unsub: (() => void) | null = null;

function dateKey(): string {
  return new Date().toDateString();
}

function recomputeIfNeeded(fix: Fix): void {
  const today = dateKey();
  const here = { lat: fix.lat, lon: fix.lon };
  const movedFar = !lastFix || haversineMeters(lastFix, here) > RECOMPUTE_DISTANCE_M;
  const dateChanged = lastDateKey !== today;
  if (!movedFar && !dateChanged) return;
  lastFix = here;
  lastDateKey = today;
  declinationStore.setState({ declDeg: declinationDeg(fix.lat, fix.lon, new Date()) });
}

export function startDeclination(): void {
  if (unsub) return;
  const current = posStore.getState().fix;
  if (current) recomputeIfNeeded(current);
  unsub = posStore.subscribe((s, prev) => {
    if (s.fix !== prev.fix && s.fix) recomputeIfNeeded(s.fix);
  });
}

export function stopDeclination(): void {
  unsub?.();
  unsub = null;
}
